import type { ApiJourneyTemplate } from '@/model/create-idf-journey.model'
import type { CreateJourneyErrors, CreateJourneyForm } from '@/model/create-journey.model'
import type { EditIdfJourney } from '@/model/edit-idf-journey.model'
import type {
  IdfExtraOptions,
  IdfJourneyTemplate,
  JourneyProduct,
} from '@/types/idf-journey-product'
import { FINAAL_LABEL, type JourneyTemplate } from '@/types/journey-template'
import type { Product } from '@/types/product'
import type { LzpUser } from '@/types/user'
import { require, type Nullish } from '@logistiek-zonder-papier/lzp-library'

export function createEmptyJourneyProduct(): Product {
  return {
    uuid: '',
    id: null,
    naam: '',
    omschrijving: null,
    gewicht: null,
    rdCode: null,
    aantalVerpakkingen: null,
    chemischeSamenstelling: null,
    createdDate: '',
    eenheid: null,
    euralCode: null,
    euralBeschrijving: null,
    euralGevaarlijk: false,
    extraOptions: null,
    fysischeEigenschappen: null,
    statischNummer: null,
    verpakkingType: null,
  }
}
export function createJourney(
  user: LzpUser | undefined,
  journeyTemplate: JourneyTemplate | Nullish
): CreateJourneyForm {
  const currentDate = new Date()

  return {
    products: [],
    carrierOrganisation: null,
    carrierDriver: null,
    remark:
      user !== undefined
        ? `Aangemaakt door ${user.firstName} ${user.lastName} op ${new Date().toLocaleDateString()}`
        : '',
    startDate: new Date(currentDate.getTime() - 600000), // 10 Minutes behind
    endDate: currentDate,
    status: journeyTemplate?.intern === false ? FINAAL_LABEL : null,
  }
}

export function validateCreateJourney(
  data: Partial<CreateJourneyForm>,
  journeyTemplate: JourneyTemplate | Nullish
): {
  formErrors: CreateJourneyErrors
  productErrors: Record<string, Partial<Record<keyof Product, string>>>
} {
  const formErrors: CreateJourneyErrors = {}
  const productErrors: Record<string, Partial<Record<keyof Product, string>>> = {}

  if (
    data.startDate != null &&
    data.endDate != null &&
    data.startDate.getTime() >= data.endDate.getTime()
  ) {
    formErrors.startDate = 'De start tijd moet voor de eind tijd vallen.'
    formErrors.endDate = 'De eind tijd moet na de start tijd vallen vallen.'
  }

  if (data.startDate == null) {
    formErrors.startDate = 'De start tijd mag niet leeg zijn.'
  } else if (isNaN(data.startDate.getTime())) {
    formErrors.startDate = 'De start tijd is in een ongeldig format.'
  }

  if (data.endDate == null) {
    formErrors.endDate = 'De eind tijd mag niet leeg zijn.'
  } else if (isNaN(data.endDate.getTime())) {
    formErrors.endDate = 'De eind tijd is in een ongeldig format.'
  } else if (new Date() < data.endDate) {
    formErrors.endDate = 'De eind tijd mag niet in de toekomst liggen.'
  }

  if (data.carrierOrganisation == null) {
    formErrors.carrierOrganisation = 'Een vervoerder moet geselecteerd zijn.'
  }

  if (data.carrierDriver == null) {
    formErrors.carrierDriver = 'Een kenteken moet zijn geselecteerd.'
  }

  if (data.remark == null || data.remark == '') {
    formErrors.remark = 'Een opmerking is verplicht.'
  }

  if (!data.products || data.products.length <= 0) {
    formErrors.products = 'Kies een product.'
  } else {
    for (let index = 0; index < data.products.length; index++) {
      const product = data.products[index]
      if (product === undefined) continue

      const errors: Partial<Record<keyof Product, string>> = {}

      if (product.gewicht == null || String(product.gewicht) == '') {
        errors['gewicht'] = 'Gewicht mag niet leeg zijn.'
      } else if (product.gewicht < 0) {
        errors['gewicht'] = 'Gewicht mag niet negatief zijn.'
      } else if (product.gewicht > 10000000) {
        errors['gewicht'] = 'Gewicht mag niet groter dan 1000000 zijn.'
      }

      if (product.rdCode == null) {
        errors['rdCode'] = 'Een RD Code moet worden geselecteerd.'
      }

      if (product.uuid == '') {
        errors['uuid'] = 'Een product moet worden geselecteerd.'
      }
      if (Object.keys(errors).length > 0) productErrors[index] = errors
    }
  }

  if (data.status == null) {
    formErrors.status = 'Kies een status.'
  } else if (journeyTemplate?.intern === false && data.status.id !== 'FINAAL') {
    formErrors.status = 'Externe ritten mogen alleen de status "FINAAL" krijgen.'
  }

  return { formErrors, productErrors }
}

export function setTouched(value: boolean): Record<keyof CreateJourneyForm, boolean> {
  return {
    carrierOrganisation: value,
    products: value,
    carrierDriver: value,
    remark: value,
    startDate: value,
    endDate: value,
    status: value,
  }
}

export function createEmptyJourneyTemplate(): IdfJourneyTemplate {
  return {
    stroomName: null,
    stroomProducent: null,
    verzendAdres: null,
    stroomIhm: null,
    stroomVerwerker: null,
    bestemmingAdres: null,
    transportInstructies: null,
    extraOptions: {},
    materialen: [],
    project: { projectUUID: null },
    stroomVervoerder: [],
    intern: false,
  }
}

export function createProductsToApiProducts(products: Product[]): JourneyProduct[] {
  return products.map(product => ({
    uuid: product.uuid,
    id: product.id,
    aantalVerpakkingen: product.aantalVerpakkingen,
    chemischeSamenstelling: product.chemischeSamenstelling,
    createdDate: product.createdDate,
    eenheid: product.eenheid,
    euralCode: product.euralCode,
    euralBeschrijving: product.euralBeschrijving,
    euralGevaarlijk: product.euralGevaarlijk,
    extraOptions: product.extraOptions,
    fysischeEigenschappen: product.fysischeEigenschappen,
    gewicht: product.gewicht,
    naam: product.omschrijving ?? product.naam,
    omschrijving: product.omschrijving ?? product.naam,
    rdCode: product.rdCode,
    statischNummer: product.statischNummer,
    verpakkingType: product.verpakkingType,
  }))
}

export function parseApiJourneyTemplate(data: IdfJourneyTemplate): IdfJourneyTemplate {
  return {
    stroomName: data.stroomName,
    stroomProducent: require(data.stroomProducent) ?? null,
    verzendAdres: data.verzendAdres,
    stroomIhm: data.stroomIhm,
    stroomVerwerker: data.stroomVerwerker,
    bestemmingAdres: data.bestemmingAdres,
    transportInstructies: data.transportInstructies,
    extraOptions: data.extraOptions ?? {},
    materialen: createProductsToApiProducts(data.materialen!) ?? [],
    project: { projectUUID: data.project.projectUUID },
    stroomVervoerder: data.stroomVervoerder ?? [],
    intern: false,
  }
}

export function parseEditIdfJourneyTemplate(data: EditIdfJourney): IdfJourneyTemplate {
  return {
    stroomName: data.stroomName,
    stroomProducent: data.stroomProducent,
    verzendAdres: data.verzendAdres,
    stroomIhm: data.stroomIHM ?? null,
    stroomVerwerker: data.stroomVerwerker,
    bestemmingAdres: data.bestemmingAdres,
    transportInstructies: data.transportInstructies,
    extraOptions: data.extraOptions ?? {},
    materialen: createProductsToApiProducts(data.materialen) ?? [],
    project: { projectUUID: data.project.projectUUID },
    stroomVervoerder: data.stroomVervoerder ?? [],
    intern: false,
  }
}

const toId = (obj: { id: number } | null) => ({ id: obj?.id.toString() ?? null })
const toRequiredId = (obj: { id: number }) => ({ id: obj.id.toString() })
const toUUID = (obj: { locatieUUID: string } | null) => ({
  locatieUUID: obj?.locatieUUID ?? null,
})

function mapNullable<T, R>(value: T | null | undefined, fn: (value: T) => R): R | null {
  return value == null ? null : fn(value)
}

export function converToApiPayload(
  collection: IdfJourneyTemplate,
  stroomName: string | null,
  project:
    | {
        projectUUID: string | null
      }
    | {
        id: string | null
      },
  extraIdfOptions: IdfExtraOptions | undefined
): ApiJourneyTemplate {
  return {
    stroomName: stroomName,
    stroomProducent: mapNullable(collection.stroomProducent, toId),
    verzendAdres: mapNullable(collection.verzendAdres, toUUID),
    stroomIHM: mapNullable(collection.stroomIhm, toId),
    stroomVerwerker: mapNullable(collection.stroomVerwerker, toId),
    bestemmingsAdres: mapNullable(collection.bestemmingAdres, toUUID),
    transportInstructies: collection.transportInstructies,
    extraOptions: extraIdfOptions ?? {},
    materialen: createProductsToApiProducts(collection.materialen!) ?? [],
    project: project,
    stroomVervoerder: collection.stroomVervoerder?.map(toRequiredId) ?? [],
    intern: false,
  }
}
